import { useDroppable, useDraggable } from "@dnd-kit/core";
import { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import React from "react";
import styled from "styled-components";
import { Survey, LanguageCode, surveyHelper, SurveyQuestionType, assertNever } from "../../../../../../../amorg-common";
import { surveyQuestionHelper } from "../../../../../../../amorg-common/helpers/surveyQuestionHelper";
import { moveSurveyQuestion, deleteSurveyQuestion } from "../../../../../../../api/surveyClientApi";
import { TextAvatar } from "../../../../../../../designSystem/avatar/TextAvatar";
import { Card } from "../../../../../../../designSystem/Card";
import { colors, rawColors } from '../../../../../../../designSystem/colors/color';
import { colorGenerator } from "../../../../../../../designSystem/colors/colorGenerator";
import { ToolBarButton } from "../../../../../../../designSystem/compositeComponents/ButtonToolBar";
import { Flex } from "../../../../../../../designSystem/compositeComponents/FlexHelpers";
import { PopupMenuButton } from "../../../../../../../designSystem/compositeComponents/PopupMenuButton";
import { DragButton } from "../../../../../../../designSystem/dragndrop/DragButton";
import { Icon } from "../../../../../../../designSystem/icons/Icons";
import { TooltipTheOneWrapper } from "../../../../../../../designSystem/TooltipTheOne";
import { TooltipWrapper } from "../../../../../../../designSystem/TooltipWrapper";
import { ChipPanel, InfoChip } from "../../../../../../../designSystem/Chip";
import { ToggletipTheOne } from '../../../../../../../designSystem/ToggletipTheOne';
import { Small } from "../../../../../../../designSystem/typography/Body";


type QuestionListItemProps = {
    survey: Survey,
    languageCode: LanguageCode,
    themeIndex: number,
    themeQuestionIndex: number,
    absoluteQuestionIndex: number,
    allIndicators: string[],
    disableEdit: boolean,
    conditionalQuestionIndices:number[],
    onOpenEditModal: (questionIndex:number) => void;
    onOpenConditionsModal: (questionIndex:number) => void;
    onQuestionUpdated: () => void;
};
export function QuestionListItem (props: QuestionListItemProps): JSX.Element
{
    const { isOver, setNodeRef: setDroppableNodeRef } = useDroppable({
        id: `question_${props.themeIndex}_${props.themeQuestionIndex}`,
    });
    const { attributes, listeners, setNodeRef: setDraggableNodeRef, setActivatorNodeRef, transform } = useDraggable({
        id: `question_${props.themeIndex}_${props.themeQuestionIndex}`,
    });

    const draggableStyle = transform ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    } : undefined;

    async function handleMoveQuestion (index:number, move: 'up' | 'down')
    {

        // handle between themes

        if (move === 'up')
        {

            if (index == 0 && props.themeIndex > 0)
            {
                const themeIndexAbove = props.themeIndex - 1;
                const questionsInThemeAbove = props.survey.themes[themeIndexAbove].questions.length;
                await moveSurveyQuestion(props.survey.id, props.themeIndex, index, themeIndexAbove, questionsInThemeAbove);
                props.onQuestionUpdated();
            }

            if (index > 0)
            {
                await moveSurveyQuestion(props.survey.id, props.themeIndex, index, props.themeIndex, index - 1);
                props.onQuestionUpdated();
            }
        }

        if (move === 'down')
        {

            const allThemesLength = props.survey.themes.length - 1;

            if (index == props.survey.themes[props.themeIndex].questions.length - 1 && props.themeIndex < allThemesLength)
            {
                await moveSurveyQuestion(props.survey.id, props.themeIndex, index, props.themeIndex + 1, index * 0);
                props.onQuestionUpdated();
            }

            if (index < props.survey.themes[props.themeIndex].questions.length - 1)
            {
                await moveSurveyQuestion(props.survey.id, props.themeIndex, index, props.themeIndex, index + 1);
                props.onQuestionUpdated();
            }
        }
    }

    async function handleDeleteQuestion (index:number)
    {
        const result = await deleteSurveyQuestion(props.survey.id, props.themeIndex, index);
        if (result.isSuccess)
        {
            props.onQuestionUpdated();
        }
    }

    const question = props.survey.themes[props.themeIndex].questions[props.themeQuestionIndex];

    return (
        <div
            ref={(node) => {
                setDroppableNodeRef(node);
                setDraggableNodeRef(node);
            }}
            {...attributes}
            style={{
                ...draggableStyle,
                backgroundColor: isOver ? '#f0f0f0' : undefined,
            }}>
        <Card variant="outlined" padding="8px">
                
            <Flex alignItems="center" gap={8}>
                {!props.disableEdit &&
                    <DragButton listeners={listeners as SyntheticListenerMap} setActivatorNodeRef={setActivatorNodeRef} />
                }

                <Flex flexDirection="column" gap={8} style={{ flexGrow: 1 }}>

                <InnerQuestionListItem
                    survey={props.survey}
                    absoluteQuestionIndex={props.absoluteQuestionIndex}
                    themeIndex={props.themeIndex}
                    questionIndex={props.themeQuestionIndex}
                    languageCode={props.languageCode}
                    allIndicators={props.allIndicators}
                    disableEdit={props.disableEdit}
                    onOpenEditModal={() => props.onOpenEditModal(props.themeQuestionIndex)}
                    onOpenConditionsModal={() => props.onOpenConditionsModal(props.themeQuestionIndex)}
                    onMoveQuestion={(move) => handleMoveQuestion(props.themeQuestionIndex, move)}
                    onDeleteQuestion={() => handleDeleteQuestion(props.themeQuestionIndex)}
                    onQuestionUpdated={props.onQuestionUpdated}
                    disableDelete={props.conditionalQuestionIndices.length > 0}
                />

                {props.conditionalQuestionIndices.map((conditionalQuestionIndex, index) => 
                {
                    const subQuestion = props.survey.themes[props.themeIndex].questions[conditionalQuestionIndex];
                    const options = surveyQuestionHelper.getOptions(question);
                    const triggers  = subQuestion.conditionalDisplay?.triggerValues || [];
                    const triggerTexts = triggers.map(t => options.find(o => o.value === t)?.text || t);

                    return (
                        <Card key={index} variant="lightGray" padding="8px" style={{ marginLeft: '0px' }}>
                            <Flex flexDirection="column" gap={8}>
                                
                                <ChipPanel>
                                {triggerTexts.map((text, i) => (
                                    <InfoChip key={i}>{text}</InfoChip>
                                ))}
                                <ToggletipTheOne>Vises kun hvis forrige spørgsmål har en af de valgte svarmuligheder</ToggletipTheOne>
                                </ChipPanel>
                                <InnerQuestionListItem
                                    survey={props.survey}
                                    absoluteQuestionIndex={conditionalQuestionIndex}
                                    themeIndex={props.themeIndex}
                                    questionIndex={conditionalQuestionIndex}
                                    languageCode={props.languageCode}
                                    allIndicators={props.allIndicators}
                                    disableEdit={props.disableEdit}
                                    onOpenEditModal={() => props.onOpenEditModal(conditionalQuestionIndex)}
                                    onOpenConditionsModal={() => props.onOpenConditionsModal(conditionalQuestionIndex)}
                                    onMoveQuestion={(move) => handleMoveQuestion(conditionalQuestionIndex, move)}
                                    onDeleteQuestion={() => handleDeleteQuestion(conditionalQuestionIndex)} 
                                    onQuestionUpdated={props.onQuestionUpdated}
                                />
                            </Flex>
                        </Card>
                    );
                })}
                </Flex>

            </Flex>
        </Card>
        </div>
    );
}

function InnerQuestionListItem(props: {
    survey: Survey,
    themeIndex:number,
    questionIndex:number,
    absoluteQuestionIndex: number,
    languageCode:LanguageCode,
    allIndicators:string[],
    disableEdit:boolean
    onOpenEditModal: () => void,
    onOpenConditionsModal: () => void,
    onQuestionUpdated: () => void,
    onMoveQuestion: (move:'up'|'down') => void,
    onDeleteQuestion: () => void,
    disableDelete?: boolean
}): JSX.Element
{

    const question = props.survey.themes[props.themeIndex].questions[props.questionIndex];
    const availableTriggerQuestion = surveyHelper.questionHasConditionalTriggerQuestion(props.survey.themes[props.themeIndex].questions, props.questionIndex);
    const isScoredQuestion = surveyQuestionHelper.isScoredQuestion(question);

    return (
        <QuestionGrid>
                
                <Flex alignItems="center" gap={8} style={{ fontSize: '16px', fontWeight: 400, flexGrow: 1 }}>
                    <Small style={{color:colors.text.gray.light}}>#{props.absoluteQuestionIndex+1}</Small>
                    {surveyHelper.getQuestionValue(props.survey, props.themeIndex, props.questionIndex, props.languageCode)}
                </Flex>

                <div style={{ alignSelf: 'center' }}>
                    <QuestionTypeComponent type={question.type} />
                </div>

                <div style={{ alignSelf: 'center' }}>
                    <QuestionIndicatorsComponent
                        allIndicators={props.allIndicators}
                        activeIndicators={question.indicators} />
                </div>

                <Flex alignItems="center" gap={8} style={{ alignSelf: 'center' }}>
                    <TooltipWrapper tooltip={question.canComment 
                        ? <div>Kommentarfeltet vises i besvarelsen, og respondenten kan skrive en kommentar til spørgsmålet.</div>
                        : <div>Kommentarfeltet vises ikke i besvarelsen, og respondenten kan ikke skrive en kommentar til spørgsmålet.</div>}>
                        <Icon name="comment" 
                            color={question.canComment ? colors.icon.active : colors.icon.disabled} />
                    </TooltipWrapper>

                    <TooltipWrapper tooltip={surveyQuestionHelper.isScoredQuestion(question)
                        ? <div>Spørgsmålet er scorende, og svarmulighederne har en scoreværdi.</div>
                        : <div>Spørgsmålet er ikke scorende, og svarmulighederne har ikke en scoreværdi.</div>}>
                        <Icon name="star_starFilled"
                            color={surveyQuestionHelper.isScoredQuestion(question) ? colors.icon.active : colors.icon.disabled} />
                    </TooltipWrapper>
                </Flex>

                <div style={{ alignSelf: 'center' }}>

                    {!props.disableEdit &&
                        <Flex justifyContent="flex-end">
                            
                            <ToolBarButton
                                icon="rule"
                                variant="text"
                                onClick={props.onOpenConditionsModal}
                                tooltip={
                                    isScoredQuestion
                                        ? "Spørgsmålet resulterer i en score og vil derfor altid blive vist"
                                        : availableTriggerQuestion
                                            ? "Rediger hvornår spørgsmålet vises baseret på foregående spørgsmål"
                                            : "Spørgsmålet er det første ubetingede spørgsmål i temaet og vil derfor altid blive vist"
                                }
                                disabled={surveyQuestionHelper.isScoredQuestion(question) || !availableTriggerQuestion} />

                            <ToolBarButton
                                icon="interface_actions_edit"
                                variant="text"
                                onClick={props.onOpenEditModal}
                                tooltip="Rediger spørgsmål"
                                disabled={props.disableEdit} />

                            <PopupMenuButton
                                buttonIcon="navigation_context"
                                entries={[
                                    {
                                        type: 'menuItem',
                                        text: 'Flyt spørgsmål op',
                                        icon: 'arrows_arrow_arrowUp',
                                        onClick: () => props.onMoveQuestion('up'),
                                        disabled: props.disableEdit
                                    },
                                    {
                                        type: 'menuItem',
                                        text: 'Flyt spørgsmål ned',
                                        icon: 'arrows_arrow_arrowDown',
                                        onClick: () => props.onMoveQuestion('down'),
                                        disabled: props.disableEdit
                                    },
                                    {
                                        type: 'menuItem',
                                        text: 'Slet spørgsmål',
                                        icon: 'interface_actions_delete',
                                        onClick: props.onDeleteQuestion,
                                        disabled: props.disableEdit || props.disableDelete
                                    }
                                ]}
                            />
                        </Flex>
                    }
                </div>

            </QuestionGrid>
    );
}

const QuestionGrid = styled.div`
    display: grid;
    // text | type | indicators | settings | buttons
    grid-template-columns: 1fr 140px min-content 50px 100px;
    gap: 24px;
    flex-grow: 1;
`;

function QuestionTypeComponent(props: { type:SurveyQuestionType }): JSX.Element
{
    let content: JSX.Element = <></>;

    if(props.type === "default")
    {
        content = <><span>Enighed</span><Icon size="x-small" name={"radio_button_checked"} /></>;
    }
    else if(props.type === "custom")
    {
        content = <><span>Tilpasset</span><Icon size="x-small" name={"radio_button_checked"} color={colors.icon.active} /></>;
    }
    else if(props.type === "multiple-choice")
    {
        content = <><span>Multiple choice</span><Icon size="x-small" name={"check_box"} /></>;
    }
    else
    {
        assertNever(props.type);
        content = <div>Ukendt</div>;
    }

    return <Flex alignItems="center" justifyContent="flex-end" gap={4}>{content}</Flex>;
}

function QuestionIndicatorsComponent(props: { allIndicators:string[], activeIndicators:string[] }): JSX.Element
{
    return (
        <IndicatorsGrid>
            {props.allIndicators.map((indicator, index) =>
            {
                const selected = props.activeIndicators.includes(indicator);

                const indicatorAvatar = 
                    <TextAvatar
                        key={index}
                        size="16px"
                        backgroundColor={selected ? colorGenerator.getBrightColorFromString(indicator) : 'transparent'}
                        borderColor={selected ? 'transparent' : rawColors.gray.lightExtreme}
                        text={""}
                    />

                
                return (
                    <TooltipTheOneWrapper key={index} tooltipContent={<div>{indicator}</div>}>
                        {indicatorAvatar}
                    </TooltipTheOneWrapper>
                );
            })}
        </IndicatorsGrid>
    );
}

const IndicatorsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, min-content);
    gap: 4px;
    align-items: center;
`;